import { type ReactNode } from 'react';
import { type RequestContext } from '@cx-web/domain-types';

import { type BettingInsightsUiSearchAttribute, type EsIndex } from '../types';

// Type for the context that will be passed to render function
export interface BettingInsightsSearchContext {
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  isWalled: boolean;
  repubConfig: RequestContext['repubConfig'];
  user: RequestContext['user'];
}

export interface BettingInsightsSearchConfig<TIndex extends EsIndex> {
  render(
    context: BettingInsightsSearchContext,
    data: { searchAttributes: BettingInsightsUiSearchAttribute<TIndex>[] },
  ): ReactNode;
}

/**
 * Factory function to create BettingInsightsSearch configuration (functional approach)
 * @param requestContext - The request context
 * @param config - The search configuration
 * @returns Immutable search configuration object
 */
export function createBettingInsightsSearch<TIndex extends EsIndex>(
  requestContext: RequestContext,
  config: BettingInsightsSearchConfig<TIndex>,
) {
  // Create context object that will be passed to render
  const context: BettingInsightsSearchContext = {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
    isWalled: requestContext.user.walls.length > 0,
  };

  // Create result object with wrapper method that injects context as first parameter
  const result = {
    // Wrapped method that injects context
    render: (data: { searchAttributes: BettingInsightsUiSearchAttribute<TIndex>[] }) => config.render(context, data),
  };

  return Object.freeze(result);
}
